/**
 * Users tab: the vault's users (server-side search, capped), click a row for
 * every field in a drawer. Each list / view is audited server-side
 * (`veeva_user.listed` / `veeva_user.viewed`, ids and counts only).
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Drawer,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  fieldLabel,
  getVeevaUser,
  listVeevaUsers,
  type VeevaUserDetailOut,
  type VeevaUserRowOut,
} from "@/shared/api/userAdmin";
import { fmtValue } from "./picks";

export function UsersPanel({ connectionId }: { connectionId: string }) {
  const getToken = useAccessToken();
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<VeevaUserRowOut[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<VeevaUserDetailOut | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(
    async (q: string) => {
      setLoading(true);
      try {
        const out = await listVeevaUsers(await getToken(), connectionId, q);
        setRows(out.items);
        setTotal(out.total_matched);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not load the users.");
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [connectionId, getToken],
  );

  useEffect(() => {
    void load("");
  }, [load]);

  const openUser = async (id: string) => {
    setDetailLoading(true);
    setDetail({ id, fields: {} });
    try {
      setDetail(await getVeevaUser(await getToken(), connectionId, id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the user.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Stack gap="sm">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void load(search);
        }}
      >
        <Group gap="xs" align="flex-end">
          <TextInput
            label="Search"
            placeholder="Name, username, email or id"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            leftSection={<IconSearch size={14} />}
            style={{ flex: 1 }}
          />
          <Button type="submit" loading={loading}>
            Search
          </Button>
        </Group>
      </form>
      {error && (
        <Alert color="red" variant="light">
          {error}
        </Alert>
      )}
      {rows === null ? (
        <Loader size="sm" />
      ) : (
        <>
          <Text size="xs" c="dimmed">
            {total > rows.length
              ? `Showing ${rows.length} of ${total} matching users — narrow the search to see others.`
              : `${rows.length} user${rows.length === 1 ? "" : "s"}`}
          </Text>
          <ScrollArea type="auto">
            <Table striped highlightOnHover withTableBorder fz="sm" data-testid="veeva-users">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Id</Table.Th>
                  <Table.Th>Username</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Security profile</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((u) => (
                  <Table.Tr
                    key={u.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => void openUser(u.id)}
                  >
                    <Table.Td>{u.id}</Table.Td>
                    <Table.Td>{u.username ?? "—"}</Table.Td>
                    <Table.Td>{u.name ?? "—"}</Table.Td>
                    <Table.Td>{u.email ?? "—"}</Table.Td>
                    <Table.Td>{u.security_profile ?? "—"}</Table.Td>
                    <Table.Td>
                      <Badge color={u.active === false ? "gray" : "green"} variant="light">
                        {u.active === false ? "Inactive" : "Active"}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </>
      )}
      <Drawer
        opened={detail !== null}
        onClose={() => setDetail(null)}
        position="right"
        size="lg"
        title={detail ? `Veeva user ${detail.id}` : ""}
      >
        {detailLoading ? (
          <Loader size="sm" />
        ) : (
          detail && (
            <Table fz="sm" withTableBorder data-testid="veeva-user-detail">
              <Table.Tbody>
                {Object.entries(detail.fields)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([k, v]) => (
                    <Table.Tr key={k}>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {fieldLabel(k)}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {k}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ wordBreak: "break-word" }}>{fmtValue(v)}</Table.Td>
                    </Table.Tr>
                  ))}
              </Table.Tbody>
            </Table>
          )
        )}
      </Drawer>
    </Stack>
  );
}
